import { HttpClient } from "@angular/common/http";
import { Injectable, inject, signal } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { firstValueFrom } from "rxjs";

import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { environment } from "src/environments/environment";

export interface ChatModel {
  name: string;
  /** Rapide, Polyvalent ou Approfondi : jamais le nom technique du modèle à l'écran. */
  label: string;
  /** Coût du niveau, affiché avec la jauge : « Coût faible »… */
  description?: string;
  niveau?: string;
  /** Jauge de coût, de 1 à 3 points. */
  cout?: number;
  defaut: boolean;
  prix_entree: number | null;
  prix_sortie: number | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  model: string;
  updated_at: string;
  /** Coût depuis la création ; absent sur une conversation tout juste créée. */
  cout_chf?: number;
}

/** Coût d'une conversation : quelques centimes en général, « < 0.01 » plutôt qu'un 0.00 trompeur. */
export function conversationCost(chf: number | undefined): string {
  if (!chf) return "";
  return chf < 0.01 ? "< 0.01 CHF" : `${chf.toFixed(2)} CHF`;
}

export interface PendingAction {
  titre: string;
  action: string;
  lignes: string[];
}

export interface UsageTotal {
  depuis: string;
  cout_chf: number;
  appels: number;
}

export interface ChannelUsage {
  canal: string;
  cout_chf: number;
  appels: number;
}

/** Canaux de l'assistant. Avant la distinction mobile, l'app mobile était comptée dans « web ». */
export const CHANNEL_LABELS: Record<string, string> = {
  web: "Web",
  mobile: "Mobile",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
};

/** Vue superadmin : en plus, l'usage des superadmins EVA-IT dans l'espace des clients (jamais compté à la société). */
export const SUPERADMIN_CHANNEL_LABELS: Record<string, string> = {
  ...CHANNEL_LABELS,
  superadmin: "Superadmin EVA-IT",
};

export interface MyUsage extends UsageTotal {
  par_canal: ChannelUsage[];
}

export interface UsageHistory {
  annee: number;
  /** Années disponibles, la plus récente d'abord. */
  annees: number[];
  mois: { mois: number; cout_chf: number; appels: number }[];
}

export interface CompaniesUsage {
  debut: string;
  fin: string;
  societes: {
    company_id: string | null;
    cout_chf: number;
    appels: number;
    utilisateurs: number;
    par_canal: ChannelUsage[];
  }[];
}

export interface CompanyUsage extends UsageTotal {
  par_employe: { employee_id: string | null; cout_chf: number; appels: number; par_canal: ChannelUsage[] }[];
  par_modele: { model: string; cout_chf: number; appels: number }[];
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Nombre de photos envoyées avec le message (seul ce nombre est conservé). */
  photos?: number;
  /** Noms des PDF envoyés avec le message (leur texte, lu par l'agent, n'est pas affiché). */
  documents?: string[];
  /** Tour en échec : l'erreur reste dans le fil (un toast se perdait en changeant de page). */
  error?: boolean;
}

export interface ChatDocument {
  nom: string;
  data_url: string;
}

export interface Conversation {
  id: string;
  title: string;
  model: string;
  messages: ChatMessage[];
  pending_action: PendingAction | null;
  /** Tour encore en cours côté agent (lancé avant un F5 ou dans un autre onglet) : sa question. */
  in_progress: { texte: string; photos: number } | null;
}

export type ChatEvent =
  | { type: "outil"; libelle: string }
  | { type: "reponse"; texte: string }
  | { type: "confirmation"; action: PendingAction }
  | { type: "erreur"; message: string; error_type: string };

export class AiChatError extends Error {
  constructor(
    public readonly errorType: string,
    message: string,
  ) {
    super(message);
  }
}

// Le PAT du chat a tous les droits de l'utilisateur : chaque écriture reste validée par bouton.
// shortcut: liste recopiée de ScopeObject (EVA-BACK models/personal_token_scope.py), un nouveau
// scope côté back n'est pas accordé au chat tant qu'on ne l'ajoute pas ici.
const ALL_SCOPES = [
  "OFFER",
  "INVOICE",
  "CUSTOMER",
  "PROJECT",
  "TAG",
  "PLANNING",
  "EMPLOYEE",
  "INTERVENTION",
  "PURCHASE_ORDER",
  "CATALOG",
  "HR_SETTINGS",
  "TASK",
  "NOTE",
].flatMap((object) => [
  { object, action: "READ" },
  { object, action: "WRITE" },
]);

/** Découpe un tampon SSE en événements complets ; `rest` est la dernière ligne incomplète. */
/** Les erreurs du chat ne passent que par un toast : remontées à Sentry pour les voir en prod. */
function report(errorType: string, detail: string): void {
  Sentry.captureMessage(`Assistant : ${errorType}`, {
    level: "warning",
    tags: { ai_error_type: errorType },
    extra: { detail },
  });
}

export function parseSse(buffer: string): { events: ChatEvent[]; rest: string } {
  const lines = buffer.split("\n");
  const rest = lines.pop() ?? "";
  const events = lines.filter((l) => l.startsWith("data: ")).map((l) => JSON.parse(l.slice(6)) as ChatEvent);
  return { events, rest };
}

/**
 * Client du canal web d'EVA-Agent (voir docs-tech/specs/2026-10-15-chat-web-eva-agent.md).
 * `fetch` plutôt que HttpClient : le flux SSE doit être lu au fil de l'eau.
 */
@Injectable({ providedIn: "root" })
export class AiChatService {
  private readonly http = inject(HttpClient);
  private readonly evaCompanyService = inject(EvaCompanyService);

  /**
   * Mode auto, partagé par toutes les saisies affichées (accueil, galet, /ai) : un signal
   * rafraîchit aussi le galet, en OnPush.
   */
  readonly auto = signal(false);
  private autoLoaded: Promise<void> | null = null;

  constructor() {
    // Réglage par collaborateur et par société : relu si déjà chargé, pour la nouvelle société.
    this.evaCompanyService.companyChanged$.subscribe(() => {
      if (this.autoLoaded) {
        this.autoLoaded = null;
        this.loadAuto();
      }
    });
  }

  get configured(): boolean {
    return !!environment.agentUrl;
  }

  /** Chat accessible : agent configuré, feature de la société et accès de la personne (accueil, galet). */
  get available(): boolean {
    return this.configured && this.evaCompanyService.canUseAiAssistant();
  }

  models(): Promise<ChatModel[]> {
    return this.request("GET", "/modeles");
  }

  conversations(): Promise<ConversationSummary[]> {
    return this.request("GET", "/conversations");
  }

  conversation(id: string): Promise<Conversation> {
    return this.request("GET", `/conversations/${id}`);
  }

  createConversation(model: string | null): Promise<ConversationSummary> {
    return this.request("POST", "/conversations", { model });
  }

  /** Mode auto : les modifications de l'assistant s'appliquent sans validation (réglage du collaborateur, tous canaux). */
  preferences(): Promise<{ auto: boolean }> {
    return this.request("GET", "/preferences");
  }

  /** Une seule lecture pour toutes les saisies ; réglage secondaire, son échec laisse le mode manuel. */
  loadAuto(): Promise<void> {
    this.autoLoaded ??= this.preferences().then(
      (preferences) => this.auto.set(preferences.auto),
      () => {
        this.autoLoaded = null;
      },
    );
    return this.autoLoaded;
  }

  /** Appliqué tout de suite partout, rétabli si l'agent refuse. */
  async setAuto(auto: boolean): Promise<void> {
    const previous = this.auto();
    this.auto.set(auto);
    try {
      await this.request("PUT", "/preferences", { auto });
    } catch (e) {
      this.auto.set(previous);
      throw e;
    }
  }

  myUsage(): Promise<MyUsage> {
    return this.request("GET", "/usage");
  }

  companyUsage(): Promise<CompanyUsage> {
    return this.request("GET", "/usage/societe");
  }

  /** Coût par mois d'une année (année en cours si omise) : le sien, ou celui de la société pour un admin. */
  usageHistory(company: boolean, annee?: number): Promise<UsageHistory> {
    return this.request("GET", `/usage${company ? "/societe" : ""}/historique${annee ? `?annee=${annee}` : ""}`);
  }

  /** Usage de toutes les sociétés, dates `AAAA-MM-JJ` incluses (superadmins). */
  companiesUsage(debut: string, fin: string): Promise<CompaniesUsage> {
    return this.request("GET", `/usage/superadmin?debut=${debut}&fin=${fin}`);
  }

  /** Bouton Stop : l'agent arrête le tour à la prochaine étape (une étape lancée va au bout). */
  stopConversation(id: string): Promise<void> {
    return this.request("POST", `/conversations/${id}/stop`);
  }

  deleteConversation(id: string): Promise<void> {
    return this.request("DELETE", `/conversations/${id}`);
  }

  /** Pairing automatique : l'utilisateur approuve pour lui-même, sans rien saisir. */
  async ensureConnected(): Promise<void> {
    const first = await this.request<{ connecte: boolean; user_code?: string }>("POST", "/connexion");
    if (first.connecte) return;
    await firstValueFrom(
      this.http.post(`${environment.apiUrl}/pairing/approve`, {
        user_code: first.user_code,
        employee_id: this.evaCompanyService.getCurrentEvaEmployee().employee_id,
        scopes: ALL_SCOPES,
      }),
    );
    const second = await this.request<{ connecte: boolean }>("POST", "/connexion");
    if (!second.connecte) throw new AiChatError("PAIRING_FAILED", "Connexion à EVA impossible pour l'instant.");
  }

  send(
    conversationId: string,
    texte: string,
    images: string[],
    documents: ChatDocument[],
    signal?: AbortSignal,
  ): AsyncGenerator<ChatEvent> {
    return this.stream(`/conversations/${conversationId}/messages`, { texte, images, documents }, signal);
  }

  /** Vocal (data URL) -> texte, transcrit par l'agent chez Infomaniak. */
  async transcribe(audio: string): Promise<string> {
    return (await this.request<{ texte: string }>("POST", "/transcription", { audio })).texte;
  }

  decide(conversationId: string, action: "valider" | "refuser", signal?: AbortSignal): AsyncGenerator<ChatEvent> {
    return this.stream(`/conversations/${conversationId}/decision`, { action }, signal);
  }

  /**
   * Lit un flux de l'agent jusqu'au bout, chaque événement passant par `onEvent`.
   * `retryOnReconnect` : PAT absent ou expiré -> pairing automatique puis même flux, une seule fois.
   * Un message peut être renvoyé, jamais une décision.
   */
  async play(
    start: (signal: AbortSignal) => AsyncGenerator<ChatEvent>,
    signal: AbortSignal,
    onEvent: (event: ChatEvent) => void,
    retryOnReconnect: boolean,
  ): Promise<void> {
    let reconnect = false;
    try {
      // Pas de `for await` : la build de prod (async/await abaissé pour zone.js puis minifié)
      // remplaçait l'erreur levée par `undefined`, d'où un 412 jamais suivi du ré-appairage.
      const events = start(signal);
      for (let next = await events.next(); !next.done; next = await events.next()) {
        const event = next.value;
        if (event.type === "erreur" && event.error_type === "NOT_CONNECTED" && retryOnReconnect) reconnect = true;
        else {
          if (event.type === "erreur") report(event.error_type, event.message);
          onEvent(event);
        }
      }
    } catch (e) {
      if (!(e instanceof AiChatError && e.errorType === "NOT_CONNECTED" && retryOnReconnect)) {
        if (!signal.aborted) report(e instanceof AiChatError ? e.errorType : "NETWORK", String(e));
        throw e;
      }
      reconnect = true;
    }
    if (reconnect) {
      await this.ensureConnected();
      await this.play(start, signal, onEvent, false);
    }
  }

  private headers(): Record<string, string> {
    // Même source que l'intercepteur HTTP : session (par onglet) d'abord, sinon local.
    const jwt = sessionStorage.getItem("evaJwt") ?? localStorage.getItem("evaJwt") ?? "";
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
      "eva-company-id": this.evaCompanyService.getCurrentEvaCompany()?.eva_company_id ?? "",
    };
  }

  private async fetchOk(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const response = await fetch(`${environment.agentUrl}/web${path}`, {
      method,
      headers: this.headers(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new AiChatError(error.error_type ?? "HTTP_" + response.status, error.error ?? "Erreur de l'assistant.");
    }
    return response;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await this.fetchOk(method, path, body);
    return response.status === 204 ? (undefined as T) : response.json();
  }

  private async *stream(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
    const response = await this.fetchOk("POST", path, body, signal);
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      const parsed = parseSse(buffer + decoder.decode(value, { stream: true }));
      buffer = parsed.rest;
      for (const event of parsed.events) yield event;
    }
  }
}

/** Fichier -> data URL. */
export function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Photo ramenée à 1600 px de côté en JPEG : lisible par le modèle, quelques centaines de Ko. */
export async function shrinkPhoto(file: File, maxSide = 1600): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}
